import { Modal, Select, Switch, Table } from 'antd';
import { forwardRef, useImperativeHandle, useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { inject, observer } from 'mobx-react';
import { formatPriceThousands } from '@/utils/configs/price.config';
import { X_Language, isMobile, toggleGlobalScroll, isEmpty } from '@/utils';
import { ProductTracker } from '@/utils/Tracker';
import ossImgCompress from '@/utils/ossImgCompress';
import { throttle } from 'lodash';
import styles from './DiffTable.module.less';

const DiffTable = forwardRef((props, ref) => {
  const { LANG, country } = props.area;
  const [visible, setVisible] = useState(false);
  const [isScrolled, setScrolled] = useState(false); // 内容块是否滚动过

  const {
    product: { productDetail = {}, packingListPoints },
  } = props;
  const { VersionConfig = {} } = productDetail; // 取套餐/规格/属性名
  const { commodityGroup, pointGroup } = packingListPoints;
  const [selectCommodity, setSelectCommodity] = useState([]); // 展示的套餐，行信息两个
  const [showSame, setShowSame] = useState(true); // 是否展示相同的选项
  const [viewportHeight, setVPH] = useState(650);
  const contentRef = useRef(null);
  const timerRef = useRef(null);
  // 配件的展示类型content_type为bool则展示图片
  const yes_img = 'https://static.insta360.com/assets/www/project/official/product/compare/comparison_yes@2x.png';
  const no_img = 'https://static.insta360.com/assets/www/project/official/product/compare/comparison_no@2x.png';
  const cameraPlaceholder =
    'https://static.insta360.com/assets/storage/20191218/f2a97117d5c7b21d3e9f9b28dad343f5/1.png?x-oss-process=image%2Fauto-orient%2C1%2Fformat%2Cwebp';

  useEffect(() => {
    if (!isEmpty(commodityGroup)) {
      setSelectCommodity(
        commodityGroup?.slice(0, 2).map((o) => {
          o.key = o.id;
          return o;
        })
      );
    }
  }, [commodityGroup]);

  /** 选择变化套餐，同时相同point_id变化 */
  const onSelect = useCallback(
    (val, index) => {
      if (val) {
        const data = commodityGroup.find((c) => c.id === val);
        selectCommodity[index] = data;
        setSelectCommodity(
          selectCommodity.slice(0).map((o) => {
            o.key = o.id;
            return o;
          }) || []
        );
        ProductTracker.inTheBoxModal({
          action: 'select_commodity',
          productState: props.product,
        });
      }
    },
    [commodityGroup, selectCommodity]
  );

  // 获取视窗高度，避免移动端浏览器的工具栏/地址栏影响
  useEffect(() => {
    const handleResize = throttle(() => {
      setVPH(window.visualViewport ? window.visualViewport.height : window.innerHeight);
    }, 200);
    window.addEventListener('scroll', handleResize);
    return () => window.removeEventListener('scroll', handleResize);
  }, []);

  /** 相同项的列point_ID 和  */
  const getSamePointIds = useMemo(() => {
    if (isEmpty(selectCommodity)) return [];
    const samePointId = []; // 筛选相同项
    const nonExistId = []; // 筛选全部都不存在的配件
    const keys = [];
    selectCommodity.forEach((c) => c.diff.forEach((d) => !keys.includes(d.point_id) && keys.push(d.point_id)));
    keys.forEach((key) => {
      const diffMatchKey = selectCommodity
        .map((commodity) => {
          return commodity.diff.find((d) => d.point_id === key);
        })
        .filter((d) => !!d);
      if (diffMatchKey.every((d) => d.content === diffMatchKey[0].content)) {
        samePointId.push(key);
      }

      if (
        (!diffMatchKey[0] || diffMatchKey[0]?.content <= 0) &&
        (!diffMatchKey[1] || diffMatchKey[1]?.content <= 0) &&
        (!diffMatchKey[2] || diffMatchKey[2]?.content <= 0)
      ) {
        nonExistId.push(key);
      }
    });
    return { samePointId, nonExistId };
  }, [selectCommodity]);

  // 隐藏相同的列选项
  const onHideSame = useCallback(() => {
    setShowSame(!showSame);
    ProductTracker.inTheBoxModal({
      action: 'click_show_difference',
      productState: props.product,
    });
  }, [productDetail, showSame]);

  useEffect(() => {
    const onScroll = (e) => {
      if (e.target.scrollTop > 0) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    if (!visible) {
      timerRef.current && clearTimeout(timerRef.current);
      return;
    }

    // 使用定时器确保事件监听会在ref挂载后正确添加
    const checkContentRef = () => {
      if (contentRef.current) {
        timerRef.current && clearTimeout(timerRef.current);
        contentRef.current.addEventListener('scroll', onScroll);
      } else {
        timerRef.current = setTimeout(checkContentRef, 100);
      }
    };

    checkContentRef();

    return () => {
      contentRef.current?.removeEventListener('scroll', onScroll);
      timerRef.current && clearTimeout(timerRef.current);
    };
  }, [visible, selectCommodity, showSame]);

  useEffect(() => {
    setVPH(window.visualViewport ? window.visualViewport.height : window.innerHeight);
  }, []);

  // 获取视窗高度，避免移动端浏览器的工具栏/地址栏影响
  const handleResize = throttle(() => {
    setVPH(window.visualViewport ? window.visualViewport.height : window.innerHeight);
  }, 100);
  const onShow = () => {
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize);
    setVisible(true);
    toggleGlobalScroll(true);
    // setVPH(window.innerHeight);
    // console.log('height', window.innerHeight);
    // console.log('visual', window.visualViewport.height);
    ProductTracker.inTheBoxModal({
      action: 'expose_pop',
      productState: props.product,
    });
  };

  const onCancel = () => {
    setVisible(false);
    toggleGlobalScroll(false);
    window.removeEventListener('resize', handleResize);
    window.removeEventListener('scroll', handleResize);
  };

  useImperativeHandle(ref, () => {
    return {
      onShow,
    };
  });

  if (isEmpty(selectCommodity)) return <></>;
  return (
    <Modal
      width={720}
      open={visible}
      closable={false}
      centered
      destroyOnClose
      footer={null}
      wrapClassName={styles.wrap}
      maskClosable
      onCancel={onCancel}
    >
      <div
        className={styles.header}
        style={isScrolled ? { boxShadow: 'rgba(112, 127, 150, 0.15) 0px 4px 10px -2px' } : {}}
      >
        <div className={styles.close} onClick={onCancel}>
          ×
        </div>
        <div className={styles.title}>{LANG['productNew.whatsInTheBoxModal']}</div>
        <div className={styles.headerSelect}>
          {commodityGroup.length > 2
            ? selectCommodity.map((co, ind) => (
                // eslint-disable-next-line react/jsx-indent
                <Select
                  className={styles.select}
                  popupClassName={styles.selectDropdown}
                  value={co?.id}
                  key={`select-${co?.id}`}
                  onChange={(val) => onSelect(val, ind)}
                  onDropdownVisibleChange={(open) =>
                    open &&
                    ProductTracker.inTheBoxModal({
                      action: 'click_toggle_commodity',
                      productState: props.product,
                    })
                  }
                  style={isMobile() ? { maxWidth: 175 } : { width: 288 }}
                >
                  {commodityGroup?.map((item = {}) => {
                    const disabled = !isEmpty(selectCommodity?.filter((c) => c.id === item.id));
                    const isSelect = co?.id === item.id;
                    if (item?.name?.includes('||') && VersionConfig.isSpecificationGroup && item.product === 17) {
                      item.name = item.name?.split('||')[1];
                    }
                    return (
                      <Select.Option value={item.id} key={item.id} disabled={disabled} className={styles.selectOption}>
                        <span
                          className={styles.selectOptionName}
                          style={isSelect ? { color: 'rgba(0, 0, 0, 0.85)' } : {}}
                        >
                          {item.name}
                        </span>
                        {isSelect && (
                          <img
                            src='https://res.insta360.com/static/75542cc45397bd4909e6d48da89a5745/Frame.svg'
                            alt=''
                            style={{ width: 20, height: 20 }}
                          />
                        )}
                      </Select.Option>
                    );
                  })}
                </Select>
              ))
            : selectCommodity.map((co, ind) => (
                // eslint-disable-next-line react/jsx-indent
                <div className={styles.commodity_name} key={`select-${co?.id}`}>
                  {co.name}
                </div>
              ))}
        </div>
      </div>
      <div
        className={styles.content}
        ref={contentRef}
        style={
          isMobile()
            ? {
                maxHeight: `calc(${viewportHeight}px - 236px)`,
              }
            : {}
        }
      >
        <div className={styles.cameraRow}>
          {selectCommodity.map((co, ind) => {
            const image_url = co.displays[0]?.url || cameraPlaceholder;
            const price = co.price || {};
            const origin_price = co.origin_price || {};
            const commodity = productDetail.commodities.find((d) => d.id === co.id);
            const canSale = commodity?.stock > 0 && commodity?.sale_state >= 0;
            return (
              <div className={styles.cameraWrap}>
                <div className={styles.camera}>
                  <img className={styles.master} src={ossImgCompress(image_url, { webp: true })} alt='' />
                </div>
                <div className={styles.priceCol}>
                  {co.enabled && canSale ? (
                    <>
                      <span>
                        {formatPriceThousands({
                          price: price.amount,
                          signal: price.signal,
                          currency: price.currency,
                          country,
                        })}
                      </span>
                      {origin_price.amount !== price.amount && (
                        <span>
                          {formatPriceThousands({
                            price: origin_price.amount,
                            signal: origin_price.signal,
                            currency: origin_price.currency,
                            country,
                          })}
                        </span>
                      )}
                    </>
                  ) : (
                    <span>
                      {formatPriceThousands({
                        price: origin_price.amount,
                        signal: origin_price.signal,
                        currency: origin_price.currency,
                        country,
                      })}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {pointGroup?.map((point = {}) => {
          if (!showSame && getSamePointIds.samePointId?.includes(point.id)) return;
          if (getSamePointIds.nonExistId?.includes(point.id)) return;

          return (
            <div className={styles.pointRow}>
              {selectCommodity.map((co) => {
                const curDiffData = co?.diff.find((d) => d.point_id === point.id) || {};
                const isAbsence = curDiffData?.content_type !== 'bool' && curDiffData?.content <= 0;
                return (
                  <div className={styles.pointWrap}>
                    <img
                      className={styles.pointImg}
                      data-abs={isAbsence}
                      src={ossImgCompress(point.image_url, { webp: true, quality: 90 })}
                      alt=''
                    />
                    <div className={styles.pointText}>
                      <div className={styles.pointName} data-abs={isAbsence}>
                        {point.name}
                      </div>
                      <div className={styles.pointNum}>
                        {curDiffData?.content_type === 'bool' ? (
                          <img
                            className={styles.boolImg}
                            src={curDiffData.content === 'true' ? yes_img : no_img}
                            alt=''
                          />
                        ) : curDiffData?.content > 0 ? (
                          `× ${curDiffData.content}`
                        ) : (
                          <div>—</div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      {commodityGroup?.length > 1 && (
        <div className={styles.footer}>
          <div className={styles.headerText}>
            <span>{LANG['product.overview.in_the_box.switch_showSame']}</span>
            <Switch checked={!showSame} onChange={onHideSame} />
          </div>
        </div>
      )}
    </Modal>
  );
});

export default inject('area', 'order', 'product')(observer(DiffTable));
